<script setup>
/**
 * 场景路由页 —— 这个后台被立项的那件事就在这一页上：把 `kp` 链的主力从 A 模型换成 B，点保存，
 * 不重启服务，下一次游戏调用就用 B（后端写事务提交后 bump 配置版本 + `llm.invalidate()`，
 * mode-router 的实例缓存随之重建）。
 *
 * 本页是**容器**：取数据、每个场景排一个 Section、卡里存完之后把返回的那一行换进列表。一条链怎么编辑在
 * `mode/ChainEditor.vue`，一个场景怎么存、被拒了怎么办在 `mode/ModeCard.vue`。
 *
 * 可选的 (厂商, 模型) 要一个个厂商详情去取 —— 列表接口只给数量，不给模型名。配置表就几十行，
 * 这几个请求并发发一次，比给后端加一个只有这一页用的聚合接口划算。
 */
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Button, EmptyState, PageHeader, Section, Spinner } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { fetchModelCapabilityCatalog } from '../../api/model-capabilities.js'
import { listLlmModes } from '../../api/llm/index.js'
import { chainOptions } from './mode-chain.js'
import ModeCard from './mode/ModeCard.vue'

const { t } = useI18n()
const router = useRouter()

/** @type {import('vue').Ref<import('../../api/llm/index.js').ModeRow[]>} */
const modes = ref([])
/** @type {import('vue').Ref<import('./mode-chain.js').ChainStep[]>} */
const options = ref([])
const loading = ref(false)
const error = ref('')

const noOptions = computed(() => options.value.length === 0)

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [modeList, catalog] = await Promise.all([listLlmModes(), fetchModelCapabilityCatalog()])
    modes.value = modeList.items
    options.value = chainOptions(catalog)
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

/** 保存成功后后端返回的就是新的那一行，直接换进列表 —— 不再回查一次。 */
function onModeSaved(saved) {
  modes.value = modes.value.map((mode) => (mode.mode === saved.mode ? { ...saved, required: mode.required } : mode))
}
</script>

<template>
  <div class="vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.modes.title')" />

    <Section v-if="loading">
      <div class="modes__state">
        <Spinner :label="t('visualGameAdmin.common.loading')" />
        <span>{{ t('visualGameAdmin.common.loading') }}</span>
      </div>
    </Section>

    <Section v-else-if="error">
      <div
        class="modes__state"
        role="alert"
      >
        <p class="vga-error">
          {{ error }}
        </p>
        <Button
          variant="secondary"
          @click="load"
        >
          {{ t('visualGameAdmin.common.retry') }}
        </Button>
      </div>
    </Section>

    <Section
      v-else-if="noOptions"
      data-insp-path="apps/visual-game-admin/src/pages/llm/Modes.vue#no-options"
    >
      <EmptyState
        :title="t('visualGameAdmin.llm.modes.noOptions.title')"
        :description="t('visualGameAdmin.llm.modes.noOptions.description')"
      >
        <template #action>
          <Button
            variant="primary"
            @click="router.push({ name: 'llm-providers' })"
          >
            {{ t('visualGameAdmin.llm.modes.noOptions.action') }}
          </Button>
        </template>
      </EmptyState>
    </Section>

    <Section
      v-else-if="modes.length === 0"
      data-insp-path="apps/visual-game-admin/src/pages/llm/Modes.vue#empty"
    >
      <EmptyState
        :title="t('visualGameAdmin.llm.modes.empty.title')"
        :description="t('visualGameAdmin.llm.modes.empty.description')"
      >
        <template #action>
          <Button
            variant="primary"
            @click="router.push('/llm/capabilities')"
          >
            {{ t('visualGameAdmin.llm.modes.empty.action') }}
          </Button>
        </template>
      </EmptyState>
    </Section>

    <ModeCard
      v-for="mode in modes"
      v-else
      :key="mode.mode"
      :mode="mode"
      :options="options"
      @saved="onModeSaved"
    />
  </div>
</template>

<style scoped>
.modes__state {
  display: grid;
  justify-items: start;
  gap: var(--drama-space-md);
  color: var(--drama-color-text-muted);
}
</style>
